import api from './api';

const API_ORIGIN = import.meta.env.VITE_API_URL ?? '';

// Server-relative upload paths (e.g. "/uploads/screenshots/.../shot.jpg") must be
// served from the API origin, not the frontend origin.
export const fileUrl = (path) => {
  if (!path) return '';
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
};

export const userService = {
  list: (params) => api.get('/users', { params }).then((r) => r.data),
  birthdays: () => api.get('/users/birthdays').then((r) => r.data),
  get: (id) => api.get(`/users/${id}`).then((r) => r.data),
  update: (id, data) => api.put(`/users/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/users/${id}`).then((r) => r.data),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data).then((r) => r.data),
};

export const attendanceService = {
  checkIn: () => api.post('/attendance/check-in').then((r) => r.data),
  checkOut: () => api.post('/attendance/check-out').then((r) => r.data),
  startLunch: () => api.post('/attendance/lunch/start').then((r) => r.data),
  endLunch: () => api.post('/attendance/lunch/end').then((r) => r.data),
  today: () => api.get('/attendance/today').then((r) => r.data),
  myHistory: (params) => api.get('/attendance/me', { params }).then((r) => r.data),
  mySummary: (params) => api.get('/attendance/me/summary', { params }).then((r) => r.data),
  adminToday: () => api.get('/attendance/admin/today').then((r) => r.data),
  adminList: (params) => api.get('/attendance', { params }).then((r) => r.data),
};

export const leaveService = {
  apply: (formData) =>
    api.post('/leaves', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
  mine: (params) => api.get('/leaves/me', { params }).then((r) => r.data),
  balance: () => api.get('/leaves/balance').then((r) => r.data),
  get: (id) => api.get(`/leaves/${id}`).then((r) => r.data),
  cancel: (id, data) => api.patch(`/leaves/${id}/cancel`, data || {}).then((r) => r.data),
  adminList: (params) => api.get('/leaves', { params }).then((r) => r.data),
  decide: (id, data) => api.patch(`/leaves/${id}/decision`, data).then((r) => r.data),
};

export const holidayService = {
  list: (params) => api.get('/holidays', { params }).then((r) => r.data),
  create: (data) => api.post('/holidays', data).then((r) => r.data),
  update: (id, data) => api.put(`/holidays/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/holidays/${id}`).then((r) => r.data),
};

export const projectService = {
  list: (params) => api.get('/projects', { params }).then((r) => r.data),
  get: (id) => api.get(`/projects/${id}`).then((r) => r.data),
  create: (data) => api.post('/projects', data).then((r) => r.data),
  update: (id, data) => api.put(`/projects/${id}`, data).then((r) => r.data),
  addMembers: (id, members) => api.post(`/projects/${id}/members`, { members }).then((r) => r.data),
  remove: (id) => api.delete(`/projects/${id}`).then((r) => r.data),
  listMessages: (id, params) => api.get(`/projects/${id}/messages`, { params }).then((r) => r.data),
  sendMessage: (id, payload) => {
    if (payload instanceof FormData) {
      return api
        .post(`/projects/${id}/messages`, payload, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((r) => r.data);
    }
    return api
      .post(`/projects/${id}/messages`, typeof payload === 'string' ? { text: payload } : payload)
      .then((r) => r.data);
  },
  editMessage: (id, messageId, text) =>
    api.patch(`/projects/${id}/messages/${messageId}`, { text }).then((r) => r.data),
  deleteMessage: (id, messageId) => api.delete(`/projects/${id}/messages/${messageId}`).then((r) => r.data),
  addDocuments: (id, formData) =>
    api
      .post(`/projects/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data),
  removeDocument: (id, docId) => api.delete(`/projects/${id}/documents/${docId}`).then((r) => r.data),
};

export const taskService = {
  list: (params) => api.get('/tasks', { params }).then((r) => r.data),
  get: (id) => api.get(`/tasks/${id}`).then((r) => r.data),
  create: (data) => api.post('/tasks', data).then((r) => r.data),
  update: (id, data) => api.put(`/tasks/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/tasks/${id}`).then((r) => r.data),
  addComment: (id, data) => api.post(`/tasks/${id}/comments`, data).then((r) => r.data),
};

export const workReportService = {
  create: (formData) =>
    api.post('/work-reports', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
  mine: (params) => api.get('/work-reports/me', { params }).then((r) => r.data),
  adminList: (params) => api.get('/work-reports', { params }).then((r) => r.data),
  remove: (id) => api.delete(`/work-reports/${id}`).then((r) => r.data),
};

export const notificationService = {
  list: (params) => api.get('/notifications', { params }).then((r) => r.data),
  markRead: (id) => api.patch(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => api.patch('/notifications/read-all').then((r) => r.data),
  remove: (id) => api.delete(`/notifications/${id}`).then((r) => r.data),
  registerToken: (token) => api.post('/notifications/token', { token }).then((r) => r.data),
  unregisterToken: (token) => api.delete('/notifications/token', { data: { token } }).then((r) => r.data),
};

export const conversationService = {
  list: () => api.get('/conversations').then((r) => r.data),
  get: (id) => api.get(`/conversations/${id}`).then((r) => r.data),
  createDirect: (userId) => api.post('/conversations', { type: 'direct', userId }).then((r) => r.data),
  createGroup: (name, members) =>
    api.post('/conversations', { type: 'group', name, members }).then((r) => r.data),
  update: (id, data) => api.patch(`/conversations/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/conversations/${id}`).then((r) => r.data),
  markRead: (id) => api.post(`/conversations/${id}/read`).then((r) => r.data),
  heartbeat: (id) => api.post(`/conversations/${id}/heartbeat`).then((r) => r.data),
  clearPresence: () => api.post('/conversations/presence/clear').then((r) => r.data),
  addMembers: (id, members) => api.post(`/conversations/${id}/members`, { members }).then((r) => r.data),
  removeMember: (id, userId) => api.delete(`/conversations/${id}/members/${userId}`).then((r) => r.data),
  listMessages: (id, params) => api.get(`/conversations/${id}/messages`, { params }).then((r) => r.data),
  sendMessage: (id, payload) => {
    if (payload instanceof FormData) {
      return api
        .post(`/conversations/${id}/messages`, payload, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((r) => r.data);
    }
    return api.post(`/conversations/${id}/messages`, payload).then((r) => r.data);
  },
  editMessage: (id, messageId, text) =>
    api.patch(`/conversations/${id}/messages/${messageId}`, { text }).then((r) => r.data),
  deleteMessage: (id, messageId) =>
    api.delete(`/conversations/${id}/messages/${messageId}`).then((r) => r.data),
};

export const trackerService = {
  adminLive: () => api.get('/tracker/admin/live').then((r) => r.data),
  adminDay: (params) => api.get('/tracker/admin/day', { params }).then((r) => r.data),
  adminSummary: (params) => api.get('/tracker/admin/summary', { params }).then((r) => r.data),
  adminSummaryCSV: 'tracker/admin/summary.csv',
  adminScreenshots: (params) => api.get('/tracker/admin/screenshots', { params }).then((r) => r.data),
  removeScreenshot: (id) => api.delete(`/tracker/admin/screenshot/${id}`).then((r) => r.data),
  bulkDeleteScreenshots: (params) => api.delete('/tracker/admin/screenshots', { params }).then((r) => r.data),
  getSettings: () => api.get('/tracker/admin/settings').then((r) => r.data),
  updateSettings: (body) => api.put('/tracker/admin/settings', body).then((r) => r.data),
};

export const reportService = {
  overview: () => api.get('/reports/admin/overview').then((r) => r.data),
  performance: (params) => api.get('/reports/admin/performance', { params }).then((r) => r.data),
  attendanceCSV: 'reports/admin/attendance.csv',
  leavesCSV: 'reports/admin/leaves.csv',
  workReportsCSV: 'reports/admin/work-reports.csv',
};
